/**
 * ListRow - Mobile
 * 
 * Standard list row - 56pt minimum.
 * Structured, table-like feel with smooth press animation.
 * Supports optional badge count for notifications.
 */

import React, { useRef } from 'react';
import { View, Text, Pressable, StyleSheet, Animated } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, text, space, radius, touch, icon } from '../../../shared/tokens/mobile';

interface ListRowProps {
  iconName: keyof typeof Ionicons.glyphMap;
  iconColor?: string;
  title: string;
  subtitle?: string;
  onPress: () => void;
  hideBorder?: boolean;
  showChevron?: boolean;
  badgeCount?: number;
}

export function ListRow({
  iconName,
  iconColor = color.textSecondary,
  title,
  subtitle,
  onPress,
  hideBorder = false,
  showChevron = true,
  badgeCount,
}: ListRowProps) {
  const hasBadge = badgeCount !== undefined && badgeCount > 0;
  const scale = useRef(new Animated.Value(1)).current;

  const handlePressIn = () => {
    Animated.spring(scale, { toValue: 0.98, useNativeDriver: true, speed: 50 }).start();
  };

  const handlePressOut = () => {
    Animated.spring(scale, { toValue: 1, useNativeDriver: true, speed: 50 }).start();
  };

  return (
    <Pressable
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      accessibilityRole="button"
      accessibilityLabel={title}
    >
      <Animated.View
        style={[
          styles.row,
          !hideBorder && styles.border,
          { transform: [{ scale }] },
        ]}
      >
        <View style={styles.iconContainer}>
          <Ionicons name={iconName} size={icon.md} color={iconColor} />
          {hasBadge && (
            <View style={styles.iconBadge}>
              <Text style={styles.iconBadgeText}>{badgeCount > 9 ? '9+' : badgeCount}</Text>
            </View>
          )}
        </View>
        <View style={styles.content}>
          <Text style={styles.title} numberOfLines={1}>{title}</Text>
          {subtitle && (
            <Text style={styles.subtitle} numberOfLines={1}>{subtitle}</Text>
          )}
        </View>
        {hasBadge && (
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{badgeCount > 99 ? '99+' : badgeCount}</Text>
          </View>
        )}
        {showChevron && (
          <Ionicons name="chevron-forward" size={icon.sm + 2} color={color.textMuted} />
        )}
      </Animated.View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: touch.listRow,
    paddingVertical: space.sm + space.xs,
    paddingHorizontal: space.md,
    gap: space.sm + space.xs,
    backgroundColor: color.surface,
  },
  border: {
    borderBottomWidth: 1,
    borderBottomColor: color.divider,
  },
  pressed: {
    backgroundColor: color.pressed,
  },
  iconContainer: {
    position: 'relative',
  },
  iconBadge: {
    position: 'absolute',
    top: -4,
    right: -6,
    minWidth: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: color.error,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 2,
  },
  iconBadgeText: {
    fontSize: 9,
    fontWeight: '700',
    color: color.textInverse,
  },
  content: {
    flex: 1,
  },
  title: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    lineHeight: text.body.lineHeight,
    color: color.textPrimary,
  },
  subtitle: {
    fontSize: text.caption.fontSize,
    fontWeight: text.caption.fontWeight,
    lineHeight: text.caption.lineHeight,
    color: color.textSecondary,
    marginTop: space.xs / 2,
  },
  badge: {
    minWidth: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: color.error,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: space.xs,
  },
  badgeText: {
    fontSize: text.caption.fontSize - 1,
    fontWeight: '600',
    color: color.textInverse,
  },
});
